import {
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { createAPIKey, getAPIKeys } from '../../../../redux/slices/apiKey';
import { RootState, useSelector } from '../../../../redux/store';
import useGuardedDispatch from 'guards/useGuardedDispatch';
import { Button, TextField } from '@nps.today/component-library';
import { ApiKeyRow } from './ApiKeyRow';
import { useTranslator } from 'translation/useTranslator';
import { Text } from 'components/text/Text';
import AccessGuard, { rules } from 'access';

export default function APIKeys() {
  const translator = useTranslator();
  const guardedDispatch = useGuardedDispatch();
  const { apiKeys } = useSelector((state: RootState) => state.apiKey);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    guardedDispatch({ action: getAPIKeys, rule: rules.apiKey.read, disableSuccessMessage: true });
  }, [guardedDispatch]);

  return (
    <AccessGuard rule={rules.apiKey.read}>
      <CardHeader
        sx={{ py: 0, px: 2 }}
        subheader={
          <>
            <Text variant="body2">{translator.apiKeysDescription()}</Text>
            <Text variant="body2">{translator.youHaveXApiKeys(`${apiKeys.length}`)}</Text>
          </>
        }
        action={
          <AccessGuard rule={rules.apiKey.create}>
            <Button
              dataTestId="add-api-key"
              variant="contained"
              onClick={() => setCreateOpen(true)}
            >
              {translator.addApiKey()}
            </Button>
          </AccessGuard>
        }
      />
      <AccessGuard rule={rules.apiKey.create}>
        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth>
          <DialogTitle>{translator.addApiKey()}</DialogTitle>
          <DialogContent>
            <Typography>{translator.pleaseGiveTheApiKeyAName()}</Typography>
            <TextField
              dataTestId="add-api-key-dialog-name"
              fullWidth
              value={name}
              onChange={(event) => setName(event.target.value)}
              label={translator.name()}
              margin={'normal'}
              color={'primary'}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button dataTestId="add-api-key-dialog-cancel" onClick={() => setCreateOpen(false)}>
              {translator.cancel()}
            </Button>
            <Button
              dataTestId="add-api-key-dialog-create"
              disabled={name?.length <= 0}
              onClick={() => {
                if (!name) return;
                guardedDispatch({
                  action: createAPIKey,
                  actionParameters: name,
                  onSuccess: () => {
                    setCreateOpen(false);
                  }
                });
              }}
              autoFocus
              variant={'contained'}
            >
              {translator.create()}
            </Button>
          </DialogActions>
        </Dialog>
      </AccessGuard>
      <Stack spacing={2} sx={{ my: 2 }}>
        <ul style={{ gap: '1rem' }}>
          {apiKeys.map((apiKey, index) => (
            <div key={index}>
              <li key={index} style={{ listStyle: 'none', gap: '1rem' }}>
                <ApiKeyRow apiKey={apiKey} />
              </li>
              {apiKeys.length - 1 > index && <Divider style={{ margin: '.5rem' }} />}
            </div>
          ))}
        </ul>
      </Stack>
    </AccessGuard>
  );
}
